/**
 * The long description's blocks, tidied and read as text (ADR-055).
 *
 * Shared by the API, which stores what these return, and the admin, which
 * sends it — so "has anything changed" compares what will actually be saved,
 * and a block the editor left empty disappears the same way on both sides.
 * Limits and references (a photograph that is the product's, a type the API
 * knows) are the API's to check; this only trims and drops.
 */
const tidyText = (text) => ({
    en: typeof text?.en === 'string' ? text.en.trim() || null : null,
    ar: typeof text?.ar === 'string' ? text.ar.trim() || null : null,
});
const isWritten = (text) => text.en !== null || text.ar !== null;
function tidyHighlight(item) {
    const attribute = typeof item.attribute === 'string' ? item.attribute.trim().toLowerCase() || null : null;
    const label = tidyText(item.label);
    const value = tidyText(item.value);
    // A card written here is its value; a label with nothing under it says nothing.
    if (!attribute && !isWritten(value))
        return null;
    return { icon: item.icon ?? null, attribute, label, value: attribute ? { en: null, ar: null } : value };
}
function tidyBlock(block) {
    switch (block.type) {
        case 'text': {
            const heading = tidyText(block.heading);
            const body = tidyText(block.body);
            return isWritten(heading) || isWritten(body) ? { id: block.id, type: 'text', heading, body } : null;
        }
        case 'photos': {
            const photos = (block.photos ?? [])
                .filter((photo) => typeof photo.imageId === 'string' && photo.imageId)
                .map((photo) => ({ imageId: photo.imageId, caption: tidyText(photo.caption), closeUp: photo.closeUp === true }));
            return photos.length > 0 ? { id: block.id, type: 'photos', photos } : null;
        }
        case 'highlights': {
            const items = (block.items ?? []).map(tidyHighlight).filter((item) => item !== null);
            return items.length > 0 ? { id: block.id, type: 'highlights', items } : null;
        }
        case 'list': {
            const points = (block.points ?? []).map(tidyText).filter(isWritten);
            return points.length > 0
                ? { id: block.id, type: 'list', heading: tidyText(block.heading), style: block.style === 'numbered' ? 'numbered' : 'bulleted', points }
                : null;
        }
        case 'looks':
        case 'categories':
            return { id: block.id, type: block.type, heading: tidyText(block.heading) };
        default:
            // Not ours to judge here: the API refuses a type it does not know.
            return block;
    }
}
/**
 * Every piece of copy trimmed, empty languages as null, and blocks with
 * nothing in them dropped. No blocks left is `null` — the theme's own layout.
 */
export function tidyProductBlocks(blocks) {
    if (!blocks)
        return null;
    const tidy = blocks.map(tidyBlock).filter((block) => block !== null);
    return tidy.length > 0 ? tidy : null;
}
/**
 * The blocks' words in one language, as plain paragraphs: headings, text,
 * points and written highlights, in order. What the API keeps as the plain
 * description for search, the SEO fallback and structured data. Only that
 * language's words — a block written in English alone adds nothing to Arabic.
 */
export function productBlocksText(blocks, locale) {
    const parts = [];
    for (const block of blocks ?? []) {
        switch (block.type) {
            case 'text':
                for (const part of [block.heading[locale], block.body[locale]])
                    if (part)
                        parts.push(part);
                break;
            case 'list': {
                const points = block.points.map((point) => point[locale]).filter((point) => Boolean(point));
                if (points.length > 0) {
                    if (block.heading[locale])
                        parts.push(block.heading[locale]);
                    parts.push(points.map((point) => `• ${point}`).join('\n'));
                }
                break;
            }
            case 'highlights': {
                const lines = block.items
                    .filter((item) => !item.attribute && item.value[locale])
                    .map((item) => (item.label[locale] ? `${item.label[locale]}: ${item.value[locale]}` : item.value[locale]));
                if (lines.length > 0)
                    parts.push(lines.join('\n'));
                break;
            }
            default:
                break;
        }
    }
    return parts.length > 0 ? parts.join('\n\n') : null;
}
//# sourceMappingURL=product-blocks.js.map